/**
 * GameCanvas — owns the single RAF loop that handles both physics and rendering.
 *
 * Particles live entirely in a ref (no React state), so the loop never
 * triggers a re-render. The effect only re-registers on context/dimension
 * changes (mount + resize), not every frame.
 */

import { useEffect, useRef } from 'react';
import type { Shape, Particle } from '../utils/types';
import { useCanvas } from '../hooks/useCanvas';
import {
  clearCanvas,
  drawShape,
  drawParticles,
  drawProgressBar,
  drawFeedbackText,
  drawBackgroundGrid,
} from '../utils/canvasRenderer';
import { updateParticle } from '../gameLogic/updateParticle';
import { BACKGROUND_GRID_SIZE, PROGRESS_BAR_HEIGHT } from '../utils/constants';

interface GameCanvasProps {
  shape: Shape | null;
  particlesRef: React.MutableRefObject<Particle[]>;
  progress: number;
  feedbackText: string;
  feedbackAlpha: number;
  onCanvasClick: (x: number, y: number) => void;
}

export function GameCanvas({
  shape,
  particlesRef,
  progress,
  feedbackText,
  feedbackAlpha,
  onCanvasClick,
}: GameCanvasProps) {
  const { canvasRef, context, dimensions } = useCanvas();

  // Sync mutable props to refs so the stable RAF loop always reads fresh values
  const shapeRef = useRef(shape);
  useEffect(() => { shapeRef.current = shape; }, [shape]);

  const progressRef = useRef(progress);
  useEffect(() => { progressRef.current = progress; }, [progress]);

  const feedbackRef = useRef({ text: feedbackText, alpha: feedbackAlpha });
  useEffect(() => {
    feedbackRef.current = { text: feedbackText, alpha: feedbackAlpha };
  }, [feedbackText, feedbackAlpha]);

  // Single RAF loop: physics update → render. Only re-registers on context/resize.
  useEffect(() => {
    if (!context) return;

    let lastTime = performance.now();
    let animationId: number;

    const loop = (now: number) => {
      const deltaMs = now - lastTime;
      lastTime = now;

      // Physics: update particles in-place, cull dead ones
      const alive: Particle[] = [];
      for (const p of particlesRef.current) {
        if (updateParticle(p, deltaMs)) alive.push(p);
      }
      particlesRef.current = alive;

      // Render
      clearCanvas(context, dimensions.width, dimensions.height);
      drawProgressBar(context, progressRef.current, dimensions.width, PROGRESS_BAR_HEIGHT);
      drawBackgroundGrid(context, dimensions.width, dimensions.height, BACKGROUND_GRID_SIZE);
      drawParticles(context, particlesRef.current);


      if (shapeRef.current) {
        drawShape(context, shapeRef.current);
      }

      const fb = feedbackRef.current;
      if (fb.text && fb.alpha > 0) {
        drawFeedbackText(
          context,
          fb.text,
          dimensions.width / 2,
          dimensions.height / 2 - 100,
          fb.alpha
        );
      }

      animationId = requestAnimationFrame(loop);
    };

    animationId = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(animationId);
  }, [context, dimensions, particlesRef]); // stable — particlesRef identity never changes

  const getCanvasCoords = (clientX: number, clientY: number) => {
    if (!canvasRef.current) return null;
    const rect = canvasRef.current.getBoundingClientRect();
    return { x: clientX - rect.left, y: clientY - rect.top };
  };

  const handleClick = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const coords = getCanvasCoords(e.clientX, e.clientY);
    if (coords) onCanvasClick(coords.x, coords.y);
  };

  // Touch support for mobile
  const handleTouchEnd = (e: React.TouchEvent<HTMLCanvasElement>) => {
    e.preventDefault();
    const touch = e.changedTouches[0];
    if (!touch) return;
    const coords = getCanvasCoords(touch.clientX, touch.clientY);
    if (coords) onCanvasClick(coords.x, coords.y);
  };

  return (
    <canvas
      ref={canvasRef}
      onClick={handleClick}
      onTouchEnd={handleTouchEnd}
      className="game-canvas"
    />
  );
}
